import { sendEventToGoogleAnalytics, sendPageViewToGoogleAnalytics } from './googleAnalyticsProvider';
import { setEventEmitter } from './eventEmitters/eventEmitters.utils';
import { getAnalyticsConsent, getGdprApplies, readPublisherConsentCookie } from '../init.utils';

interface AnalyticsEvent {
  event_category: string;
  event_action: string;
  event_label: string;

  [key: string]: any;
}

interface PageViewEventToAnalytics {
  url: string;
}

export interface StateAnalyticsParams {
  articleAuthor?: string;
  articleTitle?: string;
  articlePublicationTime?: string;
  siteName?: string;
  property?: string;
  countryCode: string;
  articleContentCount?: { [key: string]: number };
  articleWordCount?: number;
  language?: string;
  pageType: string;
  articleId?: string;
  articleTemplate?: Array<string>;
  channels?: Array<string>;
  contentTags?: Array<string>;
  mainMedia?: string;
  commercialTags?: Array<string>;
  dfpProperty?: string;
  ownerUsername?: string;
  articleURL?: string;
  createdAt?: string;
  experiments?: Array<string>;
  isSlideshow?: boolean;
  finiteScrollDataExists?: boolean;
  mainCategoryEmbed?: boolean;
}

export interface ClientParams {
  platform: string;
  trafficSource: string;
  trafficSourceAndId: string;
  trafficId: string;
  mmUserIdentifier: string;
  testValue: string;
  affiliateId: string;
  trafficSourceGA: string;
}

export const createAnalyticsParams = ({
  articleAuthor,
  articleTitle,
  articlePublicationTime,
  siteName,
  countryCode,
  articleContentCount,
  articleWordCount,
  trafficId,
  language,
  pageType,
  articleId,
  articleTemplate,
  channels,
  contentTags,
  mainMedia,
  commercialTags,
  dfpProperty,
  platform,
  trafficSource,
  trafficSourceAndId,
  mmUserIdentifier,
  property,
  ownerUsername,
  articleURL,
  createdAt,
  experiments,
  isSlideshow = false,
  testValue,
  affiliateId,
  trafficSourceGA,
  finiteScrollDataExists,
  mainCategoryEmbed,
}: StateAnalyticsParams & ClientParams) => ({
  geoDetected: countryCode,
  analyticsConsent: getAnalyticsConsent(readPublisherConsentCookie()),
  markupFormat: 'web',
  gdprApplies: getGdprApplies(),
  trafficReferrer: document.referrer,
  trafficId,
  articleAuthor,
  articleTitle,
  articlePublicationTime,
  siteName,
  articleContentCount,
  articleWordCount,
  language,
  platform,
  contentTags,
  pageType,
  test: testValue,
  affiliateId,
  articleId,
  articleTemplate,
  distributionChannels: channels,
  mmUserIdentifier,
  trafficSource,
  trafficSourceAndId,
  mainMedia,
  commercialTags,
  dfpProperty,
  property,
  ownerUsername,
  articleURL: articleURL || window.location.href,
  createdAt,
  experiments,
  isSlideshow,
  trafficSourceGA,
  finiteScrollDataExists: finiteScrollDataExists || false,
  mainCategoryEmbed: !!mainCategoryEmbed,
});

export function sendPageView(params: StateAnalyticsParams) {
  window.mmClientApi = window.mmClientApi || [];
  window.mmClientApi.push('getPageData', (pageDataObject: ClientParams) => {
    sendPageViewToGoogleAnalytics(createAnalyticsParams({
      ...params,
      platform: pageDataObject.platform,
      trafficSource: pageDataObject.trafficSource,
      trafficSourceAndId: pageDataObject.trafficSourceAndId,
      trafficId: pageDataObject.trafficId,
      mmUserIdentifier: pageDataObject.mmUserIdentifier,
      testValue: pageDataObject.testValue,
      affiliateId: pageDataObject.affiliateId,
      trafficSourceGA: pageDataObject.trafficSourceGA,
    }));
  });
}

function extractGAParams(params: AnalyticsEvent) {
  const { event_category, event_action, event_label, ...additionalParams } = params;
  sendEventToGoogleAnalytics(event_category, event_action, event_label, additionalParams);
}

export function analyticsEventEmitter() {
  setEventEmitter('analytics', (params: AnalyticsEvent) => extractGAParams(params));
}

export function pageViewEventToAnalyticsEventEmitter(paramsForPageViewEvent: StateAnalyticsParams) {
  setEventEmitter('sendPageViewEventToAnalytics', (params: PageViewEventToAnalytics) => sendPageView({
    ...paramsForPageViewEvent,
    articleURL: params.url,
  }));
}

export function initAnalytics(params: StateAnalyticsParams) {
  sendPageView(params);
  analyticsEventEmitter();
  pageViewEventToAnalyticsEventEmitter(params);
}
